import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import oilProductionService from "../../services/oilProduction/oilProductionservice";
import { OilIntakeLogModel } from "../../models/oilProduction-model";
import desiriUserService from "../../services/desiriUser/userservice";

interface FilterParams {
  keywordSearch?: string | null;
  oilSeedID?: number | null;
  posID?: number | null;
  startDate?: string | null;
  endDate?: string | null;
}

interface OilProductionSearchProps {
  setLoading: (loading: boolean) => void;
  onSearch: (data: OilIntakeLogModel[], totalRecords: number, filters?: FilterParams) => void;
  onReset: () => void;
}

const OilProductionSearchRecords: React.FC<OilProductionSearchProps> = ({
  setLoading,
  onSearch,
  onReset,
}) => {
  const toast = useRef<Toast>(null);

  const [keyword, setKeyword] = useState<string | null>(null);
  const [selectedOilseed, setSelectedOilseed] = useState<number | null>(null);
  const [selectedMU, setSelectedMU] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [oilseedOptions, setOilseedOptions] = useState<{ label: string; value: number }[]>([]);
  const [muOptions, setMUOptions] = useState<{ label: string; value: number }[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const response = await oilProductionService.getAllOilSeedNames();
        setOilseedOptions(response.map((item: any) => ({ label: item.name, value: item.id })));
      } catch (err) {
        console.error("Error fetching oilseeds:", err);
      }
    })();
  }, []);

  useEffect(() => {
    const fetchMUPlants = async () => {
      try {
        const response = await desiriUserService.getAllMUPlants();

        const muList = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : response?.data?.data ?? [];

        // ONLY CHANGE: alias labels to Unit-1-KR / Unit-2-MT and normalize IDs
        const formatted = (muList ?? []).map((d: any) => {
          const id = Number(d?.posID ?? d?.PosID ?? d?.id);
          const name =
            d?.posName ?? d?.PosName ?? d?.address2 ?? d?.Address2 ?? "";
          const code = String(d?.POSAlias ?? d?.posAlias ?? "").toUpperCase().trim();

          let label = name;
          const up = name.toUpperCase().replace(/\s+/g, " ");
          if (id === 3 || code === "KRN1" || /\bK\s*R\s*NAGAR\b/.test(up) || up.includes("KR NAGAR")) {
            label = "Unit-1-KR";
          } else if (id === 4 || code === "MGT" || up.includes("MAGADI TALUK") || up.includes("MAGADI")) {
            label = "Unit-2-MT";
          }

          return { label, value: id };
        });

        setMUOptions(formatted);
      } catch (error) {
        console.error("Failed to load Manufacturing Units:", error);
      }
    };

    fetchMUPlants();
  }, []);

  const handleFilter = async () => {
    setLoading(true);
    try {
      const filters: FilterParams = {
        keywordSearch: keyword,
        oilSeedID: selectedOilseed,
        posID: selectedMU,
        startDate: startDate ? formatDate(startDate) : null,
        endDate: endDate ? formatDate(endDate) : null,
      };

      const response = await oilProductionService.getAllOilIntakeLog({
        pageNumber: 1,
        pageSize: 10,
        ...filters
      });
      onSearch(response.data, response.totalRecords, filters);
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch oil intake logs.",
      });
      onSearch([], 0, {});
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setKeyword(null);
    setSelectedOilseed(null);
    setSelectedMU(null);
    setStartDate(null);
    setEndDate(null);
    onReset();
  };

  const formatDate = (date: Date) => {
    const d = new Date(date);
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
  };

  useEffect(() => {
    handleFilter();
  }, []);

  return (
    <div className="card border-noround p-3 w-full">
      <Toast ref={toast} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[150px]">
          <div className="p-input-icon-left w-full">
            <i className="pi pi-search" />
            <InputText
              value={keyword || ""}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Keyword Search"
              className="w-full"
            />
          </div>
        </div>

        <div className="flex-1 min-w-[130px]">
          <Dropdown
            value={selectedOilseed}
            options={oilseedOptions}
            onChange={(e) => setSelectedOilseed(e.value)}
            placeholder="Oilseed"
            optionLabel="label"
            className="w-full"
            showClear
          />
        </div>

        <div className="flex-1 min-w-[150px]">
          <Dropdown
            value={selectedMU}
            options={muOptions}
            onChange={(e) => setSelectedMU(e.value)}
            placeholder="Manufacturing unit"
            optionLabel="label"
            className="w-full"
            showClear
          />
        </div>

        <div className="flex-1 min-w-[120px]">
          <Calendar
            value={startDate}
            onChange={(e) => setStartDate(e.value as Date | null)}
            dateFormat="dd-mm-yy"
            placeholder="Start Date"
            className="w-full"
            readOnlyInput
          />
        </div>

        <div className="flex-1 min-w-[120px]">
          <Calendar
            value={endDate}
            onChange={(e) => setEndDate(e.value as Date | null)}
            dateFormat="dd-mm-yy"
            placeholder="End Date"
            className="w-full"
            readOnlyInput
          />
        </div>

        <Button label="Filter" onClick={handleFilter} className="min-w-[90px]" />
        <Button label="Reset" severity="success" onClick={handleReset} className="min-w-[90px]" />
      </div>
    </div>
  );
};

export default OilProductionSearchRecords;